"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Eraser, Maximize2, Minimize2, PenTool, Pencil, Trash2, X, ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PricePoint } from "@/lib/data/types";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import {
  computeKeyboardZoomStep,
  computePriceAxisTicks,
  downsample,
  filterDateRange,
  formatPriceAxisTick,
  pricePrecisionDigits,
  withTimeField,
} from "@/lib/price-chart-utils";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";

interface TrendLine {
  id: string;
  x1Pct: number;
  y1Pct: number;
  x2Pct: number;
  y2Pct: number;
}

interface FreehandPath {
  id: string;
  points: DrawPoint[];
}

interface DrawPoint {
  xPct: number;
  yPct: number;
}

function sma(points: PricePoint[], window: number): (number | undefined)[] {
  const result: (number | undefined)[] = [];
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    sum += points[i].close;
    if (i >= window) sum -= points[i - window].close;
    result.push(i >= window - 1 ? sum / window : undefined);
  }
  return result;
}

/** Kerzen-Farben - dieselben semantischen Token wie site-weit ueberall sonst
 * (app/globals.css --positive/--negative), damit eine gruene/rote Kerze dieselbe
 * Bedeutung traegt wie z.B. eine positive/negative Prozentangabe. */
const CANDLE_UP_COLOR = "#3f7a5b";
const CANDLE_DOWN_COLOR = "#b4544c";

/**
 * Kerzen-Chart (backlog.md -> [Punkt 62], "TradingView-artiges Analyse-Layout") - Recharts
 * hat keinen eingebauten Kerzen-Chart-Typ, aber das gaengige Muster funktioniert gut: ein
 * `Bar` mit `dataKey="range"` (vorberechnetes `[low, high]`-Tupel je Tag) liefert ueber
 * Recharts' eigene Y-Achsen-Skalierung bereits die korrekten Pixel-Grenzen fuer den Docht
 * (`y`/`height` entsprechen dann exakt der [low,high]-Spanne), der eigentliche Kerzenkoerper
 * (Open/Close) wird darin per eigenem `shape`-Renderer proportional dazu gezeichnet - kein
 * Blindflug mit einer selbst nachgebauten Skalierung noetig.
 */
function CandleShape(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartDataPoint;
}) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (!payload || payload.open === undefined || payload.high === undefined || payload.low === undefined) {
    return null;
  }
  const { open, high, low, close } = payload;
  if (high <= low || width <= 0) return null;
  const isUp = close >= open;
  const color = isUp ? CANDLE_UP_COLOR : CANDLE_DOWN_COLOR;
  const scale = height / (high - low);
  const bodyTop = y + (high - Math.max(open, close)) * scale;
  const bodyBottom = y + (high - Math.min(open, close)) * scale;
  const bodyHeight = Math.max(1, bodyBottom - bodyTop);
  const centerX = x + width / 2;
  const bodyWidth = Math.max(1, width * 0.6);
  return (
    <g>
      <line x1={centerX} x2={centerX} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={centerX - bodyWidth / 2} y={bodyTop} width={bodyWidth} height={bodyHeight} fill={color} />
    </g>
  );
}

function trendLinesStorageKey(symbol: string): string {
  // v2: Koordinaten sind Prozentwerte relativ zur Zeichenflaeche (siehe unten),
  // nicht mehr an Datum/Kurs gebunden - alter Schluessel wuerde inkompatible Werte liefern.
  return `ek_trendlines_v2_${symbol}`;
}

function freehandStorageKey(symbol: string): string {
  return `ek_freehand_v1_${symbol}`;
}

function generateTrendLineId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/** Radiergummi-Cursor-Modus (Feedback-Runde 16 Punkt 9, 2026-07-22): wie nah der
 * Mauszeiger (in Pixeln) an einer Linie sein muss, damit sie als Treffer zaehlt - passend
 * zum Radius des eigenen Cursor-Icons (app/globals.css -> .cursor-erase). */
const ERASE_THRESHOLD_PX = 10;

type EraseTarget = { kind: "trend"; id: string } | { kind: "freehand"; id: string };

/** Abstand Punkt-zu-Strecke (nicht zur unendlichen Geraden) - Standardformel ueber die
 * Projektion auf die Strecke, auf [0,1] geklemmt. Rein in Pixeln gerechnet (nicht in den
 * gespeicherten Prozent-Koordinaten): der Chart ist idR. deutlich breiter als hoch, ein
 * Prozent-basierter Schwellwert waere dadurch optisch verzerrt (horizontal grosszuegiger
 * als vertikal) statt eine gleichmaessige, kreisrunde Trefferzone zu ergeben. */
function distanceToSegmentPx(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** Naechstgelegene Trendlinie/Freihand-Zeichnung innerhalb von ERASE_THRESHOLD_PX, oder
 * null. Prozent-Koordinaten werden hier mit den tatsaechlichen Container-Massen (aus
 * demselben `getBoundingClientRect()` wie die Klick-Position) in Pixel zurueckgerechnet. */
function findEraseTarget(
  xPx: number,
  yPx: number,
  width: number,
  height: number,
  trendLines: TrendLine[],
  freehandPaths: FreehandPath[],
): EraseTarget | null {
  let best: (EraseTarget & { dist: number }) | null = null;
  for (const t of trendLines) {
    const dist = distanceToSegmentPx(
      xPx,
      yPx,
      (t.x1Pct / 100) * width,
      (t.y1Pct / 100) * height,
      (t.x2Pct / 100) * width,
      (t.y2Pct / 100) * height,
    );
    if (dist <= ERASE_THRESHOLD_PX && (!best || dist < best.dist)) best = { kind: "trend", id: t.id, dist };
  }
  for (const path of freehandPaths) {
    for (let i = 0; i < path.points.length - 1; i++) {
      const a = path.points[i];
      const b = path.points[i + 1];
      const dist = distanceToSegmentPx(
        xPx,
        yPx,
        (a.xPct / 100) * width,
        (a.yPct / 100) * height,
        (b.xPct / 100) * width,
        (b.yPct / 100) * height,
      );
      if (dist <= ERASE_THRESHOLD_PX && (!best || dist < best.dist)) best = { kind: "freehand", id: path.id, dist };
    }
  }
  return best ? { kind: best.kind, id: best.id } : null;
}

interface ChartDataPoint {
  date: string;
  time: number;
  close: number;
  short: number | undefined;
  long: number | undefined;
  /** OHLC fuers Kerzen-Chart (backlog.md -> [Punkt 62], "TradingView-artiges
   * Analyse-Layout") - Yahoo liefert diese Felder bereits verlaesslich mit derselben
   * Kurshistorie wie `close` (siehe PricePoint/yahoo-provider.ts), kein neuer
   * Datenbedarf. Undefined statt erfunden, falls fuer einen einzelnen Tag doch mal
   * keiner der drei Werte vorliegt - der Kerzen-Renderer laesst diesen Tag dann
   * einfach aus statt eine falsche Kerze zu zeichnen. */
  open: number | undefined;
  high: number | undefined;
  low: number | undefined;
  /** [low, high] als Tupel fuers Kerzen-Chart - Recharts' eigene Range-Bar-Unterstuetzung
   * (`<Bar dataKey="range">`) braucht ein vorberechnetes Array-Feld, kein Funktions-dataKey.
   * Faellt auf [close, close] zurueck, wenn kein High/Low vorliegt (degeneriert dann zu
   * einer unsichtbaren Nulldocht-Kerze statt eines Recharts-internen NaN-Fehlers -
   * CandleShape() selbst lehnt einen Tag ohne echtes High/Low ohnehin explizit ab). */
  range: [number, number];
}

/**
 * Eigene, memoisierte Komponente fuer den eigentlichen Recharts-Chart: das
 * Zeichenwerkzeug aktualisiert waehrend des Mausziehens sehr hochfrequent State
 * (drawPreview, bei jedem mousemove). Ohne diese Trennung wuerde jede dieser
 * Aktualisierungen den kompletten, rechenintensiven ComposedChart (Skalen, Achsen,
 * SMA-Linien) neu rendern - das war die Ursache der spuerbaren Zeichnen-Latenz.
 * Da alle Props hier stabil/memoisiert sind, rendert React.memo diese Komponente
 * beim Zeichnen gar nicht erst neu; nur das duenne SVG-Overlay darueber aktualisiert
 * sich pro Mausbewegung.
 */
const PriceChartCore = memo(function PriceChartCore({
  chartData,
  axisTicks,
  range,
  showShort,
  showLong,
  logScale,
  showCandles,
  priceFormatter,
  onZoom,
  keyboardRefLeft,
  keyboardRefRight,
}: {
  chartData: ChartDataPoint[];
  axisTicks: number[];
  range: ChartRangeLabel;
  showShort: boolean;
  showLong: boolean;
  logScale: boolean;
  showCandles: boolean;
  priceFormatter: Intl.NumberFormat;
  /** Klick+Ziehen-Zoom (Erik 2026-07-11, Feedback-Runde 8 Punkt 4). Der Ziehen-Vorschau-
   * State (refAreaLeft/Right) bleibt bewusst LOKAL in dieser memoisierten Komponente statt
   * im Elternteil - sonst wuerde jede Mausbewegung waehrend des Ziehens denselben
   * Neu-Render-Overhead verursachen, den die Memoisierung oben eigentlich vermeiden soll
   * (siehe Kommentar zu PriceChartCore). Nur das fertige Ergebnis (`onZoom`) geht nach
   * oben, aendert sich also nur einmal pro Zieh-Vorgang, nicht pro Mausbewegung. */
  onZoom: (range: [number, number]) => void;
  /** Tastatur-Alternative zum Klick-Zieh-Zoom (backlog.md -> Accessibility-Audit,
   * "technical-chart.tsx: Klick-Zieh-Zoom hat weiterhin keine Tastatur-Alternative") -
   * anders als refAreaLeft/Right oben bewusst NICHT lokal, sondern vom Elternteil
   * hereingereicht: Tastatur-Ereignisse werden auf dem Chart-Wrapper-<div> ausserhalb
   * dieser memoisierten Komponente abgefangen (siehe TechnicalChart -> handleChartKeyDown),
   * da Tastendruecke selten genug sind, dass der dadurch ausgeloeste Re-Render dieser
   * Komponente kein Performance-Problem darstellt (anders als Mausbewegungen). */
  keyboardRefLeft: number | null;
  keyboardRefRight: number | null;
}) {
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);

  // Kursverlauf zu dicht am unteren Rand (Feedback-Runde 16 Punkt 9, 2026-07-22):
  // domain={["auto","auto"]} spannt die Y-Achse bisher exakt von Minimum bis Maximum
  // der sichtbaren Werte auf - beim Vergroessern (70vh) konnte die Kurslinie dadurch
  // fast auf der X-Achse aufliegen, kein Platz fuer eingezeichnete Trendlinien/Freihand-
  // Zuege darunter. Eigener, ueber alle SICHTBAREN Serien (Kurs + je nach Toggle 50-/
  // 200-Tage-Linie, wie schon beim bisherigen "auto") berechneter Puffer unterhalb des
  // Minimums statt eines pauschalen Prozentsatzes vom Kurswert - bleibt so fuer jede
  // Aktie/jeden Zeitraum proportional zur tatsaechlich sichtbaren Schwankungsbreite.
  const yDomainMin = useMemo(() => {
    const values: number[] = [];
    for (const p of chartData) {
      values.push(p.close);
      if (showShort && p.short !== undefined) values.push(p.short);
      if (showLong && p.long !== undefined) values.push(p.long);
      // Kerzen-Dochte (high/low) koennen ueber/unter dem reinen Schlusskurs-Band liegen -
      // ohne diese hier einzurechnen wuerden lange Dochte am oberen/unteren Rand
      // abgeschnitten wirken, sobald das Kerzen-Chart aktiv ist.
      if (showCandles && p.high !== undefined) values.push(p.high);
      if (showCandles && p.low !== undefined) values.push(p.low);
    }
    if (values.length === 0) return 0;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const buffer = (max - min) * MARGIN_RATIO || max * (MARGIN_RATIO / 3);
    // Nie unter die Haelfte des tatsaechlichen Minimums (schuetzt Pennystocks mit hoher
    // relativer Schwankungsbreite vor einem negativen/Null-Wert - waere fuer die
    // Log-Skala-Option ungueltig und fuer eine lineare Skala optisch unsinnig).
    return Math.max(min * 0.5, min - buffer);
  }, [chartData, showShort, showLong, showCandles]);

  // Auf max. einen State-Commit pro Frame gedrosselt (siehe historical-line-chart.tsx) -
  // sonst erzwingt jedes mousemove-Event einen Re-Render dieser Komponente, was das
  // Ziehen ruckeln liess (Erik-Feedback 2026-07-22, Feedback-Runde 15 Punkt 1b). Ein
  // einzelner rAF-Zyklus deckt sowohl das bestehende Ziehen-Zoom-Preview (refAreaRight)
  // als auch die neue Crosshair-Hilfslinie (hoverTime, backlog.md -> [Punkt 62]) ab -
  // Ziehen impliziert ohnehin immer auch Hovern, ein zweiter paralleler rAF-Zyklus waere
  // reine Redundanz.
  const pendingRightRef = useRef<number | null>(null);
  const hoverRafRef = useRef<number | null>(null);
  const pendingHoverRef = useRef<number | null | undefined>(undefined);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  useEffect(() => () => {
    if (hoverRafRef.current !== null) cancelAnimationFrame(hoverRafRef.current);
  }, []);
  const hoverRow = useMemo(
    () => (hoverTime === null ? undefined : chartData.find((p) => p.time === hoverTime)),
    [chartData, hoverTime],
  );

  function handleMouseDown(e: { activeLabel?: string | number }) {
    if (e?.activeLabel === undefined) return;
    setRefAreaLeft(Number(e.activeLabel));
  }
  function handleMouseMove(e: { activeLabel?: string | number }) {
    const time = e?.activeLabel === undefined ? null : Number(e.activeLabel);
    pendingHoverRef.current = time;
    if (refAreaLeft !== null && time !== null) pendingRightRef.current = time;
    if (hoverRafRef.current !== null) return;
    hoverRafRef.current = requestAnimationFrame(() => {
      hoverRafRef.current = null;
      if (pendingHoverRef.current !== undefined) setHoverTime(pendingHoverRef.current);
      if (pendingRightRef.current !== null) setRefAreaRight(pendingRightRef.current);
    });
  }
  function handleMouseLeaveChart() {
    setHoverTime(null);
  }
  function handleMouseUp() {
    if (refAreaLeft !== null && refAreaRight !== null && refAreaLeft !== refAreaRight) {
      onZoom([Math.min(refAreaLeft, refAreaRight), Math.max(refAreaLeft, refAreaRight)]);
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      {/* accessibilityLayer={false}: verhindert den gelben Browser-Fokusring beim Klicken
          (siehe ausfuehrlicher Kommentar in fair-value-simulator.tsx) - zusaetzlich zur
          globalen CSS-Absicherung in app/globals.css (.recharts-surface:focus). */}
      <ComposedChart
        data={chartData}
        margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeaveChart}
        accessibilityLayer={false}
      >
        <defs>
          <linearGradient id="techPriceFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16265C" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#16265C" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" />
        <XAxis
          dataKey="time"
          type="number"
          domain={["dataMin", "dataMax"]}
          scale="time"
          tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
          ticks={axisTicks}
          interval={0}
          tick={{ fontSize: 12, fill: "#6B7280" }}
          axisLine={{ stroke: "#ECEAE3" }}
          tickLine={false}
        />
        <YAxis
          domain={[yDomainMin, "auto"]}
          scale={logScale ? "log" : "linear"}
          allowDataOverflow={false}
          tick={{ fontSize: 12, fill: "#6B7280" }}
          tickFormatter={(v: number) => priceFormatter.format(v)}
          width={70}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          // Kerzen-Serie liefert als `value` das [low,high]-Range-Tupel (siehe
          // ChartDataPoint.range oben), nicht eine einzelne Zahl - Number([low,high])
          // ergibt in JS immer NaN (Erik 2026-08-20: "Kerze: NaN $"). Eigener Zweig zeigt
          // stattdessen Open/High/Low/Close des gehoverten Tages aus dem Payload.
          formatter={(value, name, entry) => {
            if (name === "Kerze") {
              const row = entry?.payload as ChartDataPoint | undefined;
              if (
                row?.open === undefined ||
                row?.high === undefined ||
                row?.low === undefined ||
                row?.close === undefined
              ) {
                return ["–", name];
              }
              const f = (v: number) => priceFormatter.format(v);
              return [`O ${f(row.open)} H ${f(row.high)} L ${f(row.low)} C ${f(row.close)}`, name];
            }
            return [priceFormatter.format(Number(value)), name];
          }}
          labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE")}
          cursor={{ stroke: "#16265C", strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
          contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
        />
        {/* Mitwandernde Crosshair-Hilfslinie (backlog.md -> [Punkt 62]) - Tooltips
            eingebauter `cursor` oben zeichnet bereits die vertikale Linie, hier nur die
            fehlende HORIZONTALE Ergaenzung auf Schlusskurs-Hoehe (fuer beide Chart-Arten
            einheitlich - eine an Docht-Extremen ausgerichtete Linie waere im Kerzen-Modus
            inkonsistent mit dem Linien-Modus). Rastet auf den naechsten Datenpunkt ein
            statt der rohen Maus-Pixelposition zu folgen - exaktere Werteablesung. */}
        {hoverRow && (
          <ReferenceLine
            y={hoverRow.close}
            stroke="#16265C"
            strokeOpacity={0.25}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
        {showCandles ? (
          <Bar dataKey="range" name="Kerze" shape={CandleShape} isAnimationActive={false} />
        ) : (
          <Area
            type="linear"
            dataKey="close"
            name="Kurs"
            stroke="#16265C"
            strokeWidth={1.75}
            fill="url(#techPriceFill)"
            dot={false}
            isAnimationActive={false}
          />
        )}
        {showShort && (
          <Line
            type="linear"
            dataKey="short"
            name="50-Tage-Linie"
            stroke="#3F7A5B"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {showLong && (
          <Line
            type="linear"
            dataKey="long"
            name="200-Tage-Linie"
            stroke="#C6A15B"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        )}
        {refAreaLeft !== null && refAreaRight !== null && (
          <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
        )}
        {refAreaLeft === null && keyboardRefLeft !== null && keyboardRefRight !== null && (
          <ReferenceArea x1={keyboardRefLeft} x2={keyboardRefRight} fill="#16265C" fillOpacity={0.12} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
});

// Rand unten (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt 14b) - "Standard" ersetzt
// den bisherigen festen 6%-Puffer (Runde 16 Punkt 9) als neuen, groesseren Normalzustand.
// Die zwischenzeitliche "Kompakt"-Auswahlmoeglichkeit ist wieder entfernt (backlog.md ->
// [Punkt 60], Erik: mehr Platz zum Einzeichnen eigener Linien) - "Standard" ist jetzt fix.
const MARGIN_RATIO = 0.12;

export function TechnicalChart({
  symbol,
  data,
  currency = "USD",
}: {
  symbol: string;
  data: PricePoint[];
  currency?: string;
}) {
  const [range, setRange] = useState<ChartRangeLabel>("Max");
  // Default AUS statt AN (Erik 2026-08-20: "50 und die 200 Tage Linie nicht automatisch
  // eingeblendet werden sondern... erst aktiv auswählen") - beide Linien bleiben ueber die
  // bestehenden Checkboxen weiterhin normal zuschaltbar.
  const [showShort, setShowShort] = useState(false);
  const [showLong, setShowLong] = useState(false);
  const [trendLines, setTrendLines] = useState<TrendLine[]>([]);
  const [drawMode, setDrawMode] = useState(false);
  const [drawStart, setDrawStart] = useState<DrawPoint | null>(null);
  const [drawPreview, setDrawPreview] = useState<DrawPoint | null>(null);
  const [drawSnapped, setDrawSnapped] = useState(false);
  const [freehandPaths, setFreehandPaths] = useState<FreehandPath[]>([]);
  const [freehandMode, setFreehandMode] = useState(false);
  const [isDrawingFreehand, setIsDrawingFreehand] = useState(false);
  const [currentFreehandPoints, setCurrentFreehandPoints] = useState<DrawPoint[] | null>(null);
  // Radiergummi-Cursor-Modus (Feedback-Runde 16 Punkt 9, 2026-07-22) - echte Erweiterung
  // ueber den bestehenden globalen "Alles loeschen"-Knopf (handleEraseAll) hinaus: verwandelt
  // den Mauszeiger in einen Radiergummi, der einzelne Linien direkt im Chart per Klick/Ziehen
  // loescht, statt ueber die "X"-Chips unterhalb gehen zu muessen. `eraseHoverTarget` haelt
  // fest, welche Linie gerade unter dem Cursor liegt (fuer die visuelle Vorschau vor dem
  // eigentlichen Loeschen), `isErasing` ob die Maustaste dabei gehalten wird (Wisch-Geste
  // ueber mehrere Linien in einem Zug).
  const [eraseMode, setEraseMode] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [eraseHoverTarget, setEraseHoverTarget] = useState<EraseTarget | null>(null);
  // Trendlinien-Endpunkte verschieb-/drehbar (backlog.md -> [Punkt 65], konkretisiert
  // + Klick-durchreichen-Bug gemeldet Erik 2026-08-20: "an BEIDEN Endpunkten
  // anfassbar/drehbar sein" + ein Klick auf eine bereits gezeichnete Linie darf nicht
  // stattdessen den darunterliegenden Chart zoomen/ziehen). Eigener State statt
  // Wiederverwendung von drawMode/drawStart: das Greifen eines Endpunkts ist kein
  // Zeichenwerkzeug-MODUS (kein Toggle-Button, jederzeit im normalen Ansichtsmodus
  // aktiv, schliesst sich aber weiterhin gegenseitig mit den drei Werkzeug-Modi aus,
  // siehe Griff-Rendering weiter unten).
  const overlayRef = useRef<HTMLDivElement>(null);
  const [draggingEndpoint, setDraggingEndpoint] = useState<{ lineId: string; endpoint: 1 | 2 } | null>(null);
  // Tastatur-Alternative zum Klick-Zieh-Zoom (siehe handleChartKeyDown weiter unten fuer
  // die volle Begruendung) - Deklaration hier oben (nicht direkt neben zoomRange), da der
  // Symbol-Wechsel-Reset-Block direkt darunter sie bereits zuruecksetzen muss.
  const [keyboardSelection, setKeyboardSelection] = useState<{ left: number | null; right: number | null }>({
    left: null,
    right: null,
  });
  const [logScale, setLogScale] = useState(false);
  // Kerzen-Chart (backlog.md -> [Punkt 62]) - default AUS statt den bestehenden
  // Linien-/Flaechen-Chart zu ersetzen (kein Redesign ohne Auftrag, siehe CLAUDE.md) -
  // ein neuer, optionaler zweiter Blickwinkel statt eines erzwungenen Wechsels.
  const [showCandles, setShowCandles] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dataLoadedFor, setDataLoadedFor] = useState<string | undefined>(undefined);

  // Vollbildansicht: Seiten-Scroll sperren und Esc zum Verlassen anbieten, solange
  // die Zeichenflaeche als Overlay ueber der gesamten Seite liegt.
  useEffect(() => {
    if (!isExpanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setIsExpanded(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);

  // Render-Phase-Update statt useEffect ("State an eine geaenderte Prop anpassen",
  // siehe react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes) -
  // laedt die gespeicherten Linien neu, sobald sich das Symbol aendert.
  if (typeof window !== "undefined" && symbol !== dataLoadedFor) {
    setDataLoadedFor(symbol);
    try {
      const raw = localStorage.getItem(trendLinesStorageKey(symbol));
      setTrendLines(raw ? (JSON.parse(raw) as TrendLine[]) : []);
    } catch {
      setTrendLines([]);
    }
    try {
      const raw = localStorage.getItem(freehandStorageKey(symbol));
      setFreehandPaths(raw ? (JSON.parse(raw) as FreehandPath[]) : []);
    } catch {
      setFreehandPaths([]);
    }
    setDrawMode(false);
    setDrawStart(null);
    setDrawPreview(null);
    setFreehandMode(false);
    setIsDrawingFreehand(false);
    setCurrentFreehandPoints(null);
    setEraseMode(false);
    setIsErasing(false);
    setEraseHoverTarget(null);
    setDraggingEndpoint(null);
    setKeyboardSelection({ left: null, right: null });
  }

  function persistTrendLines(next: TrendLine[]) {
    setTrendLines(next);
    try {
      localStorage.setItem(trendLinesStorageKey(symbol), JSON.stringify(next));
    } catch {
      // localStorage nicht verfuegbar (z.B. privater Modus) - Linien bleiben nur fuer diese Sitzung erhalten.
    }
  }

  function persistFreehandPaths(next: FreehandPath[]) {
    setFreehandPaths(next);
    try {
      localStorage.setItem(freehandStorageKey(symbol), JSON.stringify(next));
    } catch {
      // localStorage nicht verfuegbar (z.B. privater Modus) - Zeichnungen bleiben nur fuer diese Sitzung erhalten.
    }
  }

  /** Trendlinien-Endpunkt greifen (Erik 2026-08-20, [Punkt 65] konkretisiert): startet den
   * Zieh-Vorgang fuer GENAU diesen einen Endpunkt einer bestehenden Linie. `stopPropagation`
   * verhindert, dass derselbe Mausdruck zusaetzlich `handleOverlayMouseDown` (Radiergummi/
   * Freihand) ODER - da der Griff via `pointer-events-auto` trotz `pointer-events-none` am
   * umgebenden Overlay-Div eigenstaendig klickbar ist - den darunterliegenden Chart erreicht
   * (genau der gemeldete Bug: ein Linien-Klick loeste bisher stattdessen dessen Klick-Zieh-
   * Zoom aus, da im Ansichtsmodus ohne aktives Zeichenwerkzeug JEDER Klick auf der Zeichen-
   * flaeche bisher ungehindert zum Chart durchgereicht wurde). */
  function handleEndpointMouseDown(e: React.MouseEvent, lineId: string, endpoint: 1 | 2) {
    e.stopPropagation();
    e.preventDefault();
    setDraggingEndpoint({ lineId, endpoint });
  }

  // Fensterweite Maus-Listener statt lokaler onMouseMove/onMouseUp auf dem Overlay-Div:
  // ein schnelles Ziehen verlaesst leicht dessen Grenzen (z.B. beim Verlaengern einer Linie
  // ueber den Chartrand hinaus) - ohne dies wuerde der Zieh-Vorgang dort haengen bleiben,
  // sobald der Mauszeiger die Flaeche kurzzeitig verlaesst. `setTrendLines` (nicht
  // persistTrendLines) waehrend des Ziehens - jeder mousemove in localStorage zu schreiben
  // waere unnoetiger I/O; das Setter-Callback-Muster in handleUp liest den WIRKLICH
  // aktuellen State zum Abschluss, ohne `trendLines` in die Dependency-Liste aufnehmen zu
  // muessen (das wuerde die Listener bei jeder Zieh-Bewegung neu binden).
  useEffect(() => {
    if (!draggingEndpoint) return;

    function handleMove(e: MouseEvent) {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect || !draggingEndpoint) return;
      const xPct = clampPct(((e.clientX - rect.left) / rect.width) * 100);
      const yPct = clampPct(((e.clientY - rect.top) / rect.height) * 100);
      setTrendLines((prev) =>
        prev.map((t) => {
          if (t.id !== draggingEndpoint.lineId) return t;
          return draggingEndpoint.endpoint === 1
            ? { ...t, x1Pct: xPct, y1Pct: yPct }
            : { ...t, x2Pct: xPct, y2Pct: yPct };
        }),
      );
    }
    function handleUp() {
      setDraggingEndpoint(null);
      setTrendLines((current) => {
        try {
          localStorage.setItem(trendLinesStorageKey(symbol), JSON.stringify(current));
        } catch {
          // localStorage nicht verfuegbar (z.B. privater Modus) - Aenderung bleibt nur fuer diese Sitzung erhalten.
        }
        return current;
      });
    }
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [draggingEndpoint, symbol]);

  // Gerade Trendlinie, Freihand-Zeichnung und Radiergummi-Modus teilen sich dieselbe
  // Zeichenflaeche und schliessen sich gegenseitig aus, damit ein Klick eindeutig einem
  // Werkzeug zugeordnet ist.
  function toggleDrawMode() {
    setDrawMode((prev) => {
      const next = !prev;
      if (!next) {
        setDrawStart(null);
        setDrawPreview(null);
        setDrawSnapped(false);
      } else {
        setFreehandMode(false);
        setIsDrawingFreehand(false);
        setCurrentFreehandPoints(null);
        setEraseMode(false);
        setIsErasing(false);
        setEraseHoverTarget(null);
      }
      return next;
    });
  }

  function toggleFreehandMode() {
    setFreehandMode((prev) => {
      const next = !prev;
      if (!next) {
        setIsDrawingFreehand(false);
        setCurrentFreehandPoints(null);
      } else {
        setDrawMode(false);
        setDrawStart(null);
        setDrawPreview(null);
        setEraseMode(false);
        setIsErasing(false);
        setEraseHoverTarget(null);
      }
      return next;
    });
  }

  function toggleEraseMode() {
    setEraseMode((prev) => {
      const next = !prev;
      if (!next) {
        setIsErasing(false);
        setEraseHoverTarget(null);
      } else {
        setDrawMode(false);
        setDrawStart(null);
        setDrawPreview(null);
        setFreehandMode(false);
        setIsDrawingFreehand(false);
        setCurrentFreehandPoints(null);
      }
      return next;
    });
  }

  // Radiergummi (Feedback-Runde 15, Punkt 9): einzelne Linien/Zeichnungen liessen sich
  // bereits ueber die "X"-Chips unterhalb des Charts entfernen, aber ohne eigenes
  // Werkzeug-Icon war das schwer zu entdecken - ergaenzt einen schnellen "alles
  // loeschen"-Knopf direkt neben den Zeichenwerkzeugen (globale statt Linie-fuer-Linie-
  // Loeschung reicht laut Backlog-Notiz, die granulare Einzelentfernung bleibt zusaetzlich
  // ueber die Chips erhalten). Feedback-Runde 16 Punkt 9 (2026-07-22) ergaenzt direkt
  // darunter den echten Cursor-Modus (toggleEraseMode/eraseTarget) - beide Wege bleiben
  // nebeneinander bestehen, kein Ersatz.
  const hasDrawings = trendLines.length > 0 || freehandPaths.length > 0;
  function handleEraseAll() {
    if (!hasDrawings) return;
    persistTrendLines([]);
    persistFreehandPaths([]);
  }

  function eraseTarget(target: EraseTarget) {
    if (target.kind === "trend") {
      persistTrendLines(trendLines.filter((t) => t.id !== target.id));
    } else {
      persistFreehandPaths(freehandPaths.filter((p) => p.id !== target.id));
    }
    setEraseHoverTarget(null);
  }

  const priceDigits = useMemo(
    () => pricePrecisionDigits(data[data.length - 1]?.close),
    [data],
  );

  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
        minimumFractionDigits: priceDigits,
        maximumFractionDigits: priceDigits,
      }),
    [currency, priceDigits],
  );

  const filtered = useMemo(() => filterDateRange(data, range), [data, range]);

  // SMA auf der VOLLEN Historie berechnen (nicht erst nach dem Zeitraum-Filter), damit
  // der gleitende Durchschnitt am linken Rand des sichtbaren Ausschnitts nicht
  // faelschlich abgeschnitten/fehlend wirkt. Ausduennen (Performance) erst danach, damit
  // das 50-/200-Tage-Fenster nicht durch fehlende Zwischenpunkte verfaelscht wird.
  const chartData = useMemo(() => {
    const short = sma(data, 50);
    const long = sma(data, 200);
    const byDate = new Map(data.map((p, i) => [p.date, { short: short[i], long: long[i] }]));
    const mapped = filtered.map((p) => ({
      date: p.date,
      close: p.close,
      short: byDate.get(p.date)?.short,
      long: byDate.get(p.date)?.long,
      open: p.open,
      high: p.high,
      low: p.low,
      range: [p.low ?? p.close, p.high ?? p.close] as [number, number],
    }));
    return withTimeField(downsample(mapped, 1000, (p) => p.close));
  }, [data, filtered]);

  // Klick+Ziehen zoomt zusaetzlich zum 1J/3J/5J/10J/Max-Range-Selector in einen frei
  // waehlbaren Ausschnitt (Erik 2026-07-11, Feedback-Runde 8 Punkt 4). Nur aktiv, wenn
  // weder die Trendlinien- noch die Freihand-Zeichenflaeche das Overlay beansprucht
  // (siehe pointer-events-Logik weiter unten) - dieselben Mausereignisse duerfen nicht
  // gleichzeitig Zeichnen UND Zoomen bedeuten.
  const [zoomRange, setZoomRange] = useState<[number, number] | null>(null);
  const handleZoom = useCallback((range: [number, number]) => {
    setZoomRange(range);
    // Eine noch nicht bestaetigte Tastatur-Auswahl verwerfen, falls der Nutzer stattdessen
    // per Maus zieht - sonst wuerde die (dann veraltete) Tastatur-Auswahlflaeche nach dem
    // Loslassen der Maus wieder aufscheinen (refAreaLeft/Right werden bei MouseUp lokal auf
    // null zurueckgesetzt, wodurch der Keyboard-Fallback in PriceChartCore sonst erneut
    // greift, siehe dessen ReferenceArea-Rendering).
    setKeyboardSelection({ left: null, right: null });
  }, []);
  const visibleChartData = useMemo(() => {
    if (!zoomRange) return chartData;
    const [lo, hi] = zoomRange;
    return chartData.filter((p) => p.time >= lo && p.time <= hi);
  }, [chartData, zoomRange]);

  // Tastatur-Alternative zum Klick-Zieh-Zoom (backlog.md -> Accessibility-Audit,
  // "technical-chart.tsx: Klick-Zieh-Zoom hat weiterhin keine Tastatur-Alternative") -
  // bewusst NICHT per Vollmigration auf den gemeinsamen useDragZoom-Hook (siehe dessen
  // Docstring: "technical-chart.tsx bleibt bewusst aussen vor", Sonderarchitektur aus
  // PriceChartCore + Zeichenwerkzeug-Overlay), sondern als eigenstaendiger, paralleler
  // Tastatur-Pfad, der dieselbe bereits geteilte, reine computeKeyboardZoomStep()-Funktion
  // nutzt und am Ende denselben zoomRange-State setzt wie der bestehende Maus-Pfad - exakt
  // dasselbe Endverhalten (Shift+Pfeiltasten waehlt aus, Enter uebernimmt, Escape verwirft),
  // ohne die bestehende, funktionierende Maus-/Zeichenwerkzeug-Logik anzufassen. State
  // (keyboardSelection) ist oben bei den uebrigen Modus-States deklariert.
  function handleChartKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Nur im reinen Zoom-Modus aktiv - waehrend Zeichnen/Radiergummi soll die Tastatur
    // (noch) keine widerspruechliche zweite Bedeutung bekommen, gleiche Abgrenzung wie
    // die bestehende Maus-Ueberlagerung (Zeichenwerkzeug-Overlay faengt Mausereignisse ab).
    if (drawMode || freehandMode || eraseMode) return;
    const fullMin = chartData[0]?.time;
    const fullMax = chartData[chartData.length - 1]?.time;
    if (fullMin === undefined || fullMax === undefined) return;

    if (e.key === "Escape" && (keyboardSelection.left !== null || keyboardSelection.right !== null)) {
      // stopPropagation, damit Escape hier NUR die Auswahl verwirft - sonst wuerde
      // derselbe Tastendruck zusaetzlich den globalen Vollbild-Escape-Listener (siehe
      // useEffect oben, "isExpanded") erreichen und ungewollt auch die Vollbildansicht
      // schliessen, obwohl der Nutzer nur die Zoom-Auswahl abbrechen wollte.
      e.stopPropagation();
      setKeyboardSelection({ left: null, right: null });
      return;
    }
    if (e.key === "Enter" && keyboardSelection.left !== null && keyboardSelection.right !== null) {
      e.preventDefault();
      if (keyboardSelection.left !== keyboardSelection.right) {
        setZoomRange([
          Math.min(keyboardSelection.left, keyboardSelection.right),
          Math.max(keyboardSelection.left, keyboardSelection.right),
        ]);
      }
      setKeyboardSelection({ left: null, right: null });
      return;
    }
    if (!e.shiftKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    const { left, right } = computeKeyboardZoomStep(
      keyboardSelection,
      zoomRange,
      fullMin,
      fullMax,
      e.key === "ArrowRight" ? "right" : "left",
    );
    setKeyboardSelection({ left, right });
  }

  const isNarrow = useIsNarrowViewport();
  const axisTicks = useMemo(
    () => computePriceAxisTicks(visibleChartData, range, isNarrow ? 5 : 8).map((d) => Date.parse(d)),
    [visibleChartData, range, isNarrow],
  );

  // Zeichenflaeche ist ein Overlay unabhaengig vom Chart-Koordinatensystem: die
  // Position wird als Prozentsatz der Container-Groesse erfasst, nicht als
  // Datum/Kurs-Punkt. Dadurch laesst sich frei ueberall in der Flaeche zeichnen,
  // die Linie bleibt exakt gerade und folgt nicht dem Kursverlauf.
  function pointFromEvent(e: React.MouseEvent<HTMLDivElement>): DrawPoint {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      xPct: clampPct(((e.clientX - rect.left) / rect.width) * 100),
      yPct: clampPct(((e.clientY - rect.top) / rect.height) * 100),
    };
  }

  /** Ausrichtungshilfe fürs Zeichnen (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt
   * 14a): rastet die zweite Trendlinien-Koordinate auf die waagerechte Höhe des
   * Startpunkts ein, sobald sie nah genug dran ist - Support-/Widerstandslinien sind in
   * der technischen Analyse meist waagerecht, per Hand exakt zu treffen ist auf einem
   * kleinen Chart aber fummelig. Gibt zusätzlich zurück, ob eingerastet wurde, damit die
   * Vorschau-Linie das optisch anzeigen kann. */
  const HORIZONTAL_SNAP_THRESHOLD_PCT = 1.5;
  // Mindest-Wegstrecke, bevor Snapping ueberhaupt greift (backlog.md -> [Punkt 64]):
  // direkt nach dem ersten Klick ist der Vorschau-Punkt noch (fast) identisch mit dem
  // Startpunkt - dessen Y-Differenz liegt dann trivial innerhalb der Snap-Schwelle, obwohl
  // der Nutzer noch gar keine Richtung gewaehlt hat. Das liess den "waagerecht"-Schriftzug
  // kurz aufblitzen, bevor die Linie ueberhaupt eine erkennbare Ausdehnung hatte.
  const MIN_SNAP_DRAG_PCT = 1;
  function applyHorizontalSnap(start: DrawPoint, point: DrawPoint): { point: DrawPoint; snapped: boolean } {
    const hasMinDrag = Math.abs(point.xPct - start.xPct) >= MIN_SNAP_DRAG_PCT;
    if (hasMinDrag && Math.abs(point.yPct - start.yPct) <= HORIZONTAL_SNAP_THRESHOLD_PCT) {
      return { point: { xPct: point.xPct, yPct: start.yPct }, snapped: true };
    }
    return { point, snapped: false };
  }

  /** Pixel- statt Prozent-Koordinaten inkl. Container-Massen - fuer den Radiergummi-
   * Modus, dessen Trefferzone in echten Pixeln gleichmaessig rund bleiben soll (siehe
   * distanceToSegmentPx/findEraseTarget oben). */
  function pixelPointFromEvent(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, width: rect.width, height: rect.height };
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!drawMode) return;
    const rawPoint = pointFromEvent(e);
    if (!drawStart) {
      setDrawStart(rawPoint);
      setDrawPreview(rawPoint);
      return;
    }
    const { point } = applyHorizontalSnap(drawStart, rawPoint);
    persistTrendLines([
      ...trendLines,
      {
        id: generateTrendLineId(),
        x1Pct: drawStart.xPct,
        y1Pct: drawStart.yPct,
        x2Pct: point.xPct,
        y2Pct: point.yPct,
      },
    ]);
    setDrawStart(null);
    setDrawPreview(null);
    setDrawSnapped(false);
    setDrawMode(false);
  }

  function handleOverlayMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (eraseMode) {
      const { x, y, width, height } = pixelPointFromEvent(e);
      const target = findEraseTarget(x, y, width, height, trendLines, freehandPaths);
      setEraseHoverTarget(target);
      // Waehrend die Maustaste gehalten wird, sofort loeschen statt nur vorzuschauen -
      // ergibt eine Wisch-Geste ueber mehrere Linien in einem Zug (Backlog: "per Klick/
      // Ziehen löscht").
      if (isErasing && target) eraseTarget(target);
      return;
    }
    if (drawMode && drawStart) {
      const { point, snapped } = applyHorizontalSnap(drawStart, pointFromEvent(e));
      setDrawPreview(point);
      setDrawSnapped(snapped);
      return;
    }
    if (freehandMode && isDrawingFreehand) {
      const point = pointFromEvent(e);
      setCurrentFreehandPoints((prev) => (prev ? [...prev, point] : [point]));
    }
  }

  function handleOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (eraseMode) {
      setIsErasing(true);
      const { x, y, width, height } = pixelPointFromEvent(e);
      const target = findEraseTarget(x, y, width, height, trendLines, freehandPaths);
      if (target) eraseTarget(target);
      return;
    }
    if (!freehandMode) return;
    setIsDrawingFreehand(true);
    setCurrentFreehandPoints([pointFromEvent(e)]);
  }

  function handleOverlayMouseUp() {
    if (eraseMode) {
      setIsErasing(false);
      setEraseHoverTarget(null);
      return;
    }
    if (!freehandMode || !isDrawingFreehand) return;
    setIsDrawingFreehand(false);
    if (currentFreehandPoints && currentFreehandPoints.length > 1) {
      persistFreehandPaths([...freehandPaths, { id: generateTrendLineId(), points: currentFreehandPoints }]);
    }
    setCurrentFreehandPoints(null);
  }

  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground">Kursverlauf aktuell nicht verfügbar.</p>;
  }

  // Vollbildansicht wird per Portal direkt in document.body gerendert: eine
  // "reveal"-Animation auf einem Vorfahren (Card) hinterlaesst dauerhaft
  // transform: translateY(0), was fuer position:fixed-Kinder einen neuen
  // Containing Block erzeugt - ohne Portal waere das Overlay auf die Card
  // beschraenkt statt den ganzen Viewport abzudecken.
  const content = (
    <>
      {/* Vollbild-Werkzeugleiste (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt 14c):
          die Überschrift stand bisher als eigene Zeile ÜBER der Werkzeugleiste und
          verbrauchte zusätzlichen Platz, statt welchen für den Chart selbst freizugeben -
          jetzt kompakt in dieselbe Zeile wie die Werkzeuge integriert, eine Zeile weniger
          Chrome über dem eigentlich vergrößerten Chart. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          {isExpanded && (
            <span className="font-serif text-sm text-ink">{symbol} — Chartanalyse</span>
          )}
          <label className="flex items-center gap-1.5 text-muted-foreground">
            <input type="checkbox" checked={showShort} onChange={(e) => setShowShort(e.target.checked)} />
            50-Tage-Linie
          </label>
          <label className="flex items-center gap-1.5 text-muted-foreground">
            <input type="checkbox" checked={showLong} onChange={(e) => setShowLong(e.target.checked)} />
            200-Tage-Linie
          </label>
          <label className="flex items-center gap-1.5 text-muted-foreground">
            <input type="checkbox" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />
            Log-Skala
          </label>
          <label className="flex items-center gap-1.5 text-muted-foreground" title="Kerzen-Chart (Open/High/Low/Close je Tag) statt Flaechen-/Linien-Chart">
            <input type="checkbox" checked={showCandles} onChange={(e) => setShowCandles(e.target.checked)} />
            Kerzen-Chart
          </label>
          <button
            type="button"
            onClick={toggleDrawMode}
            aria-pressed={drawMode}
            aria-label="Trendlinie zeichnen"
            title="Trendlinie zeichnen: Startpunkt anklicken, Maus bewegen, Endpunkt anklicken"
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
              drawMode ? "border-ink bg-ink text-card" : "border-hairline text-ink-secondary hover:bg-muted",
            )}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={toggleFreehandMode}
            aria-pressed={freehandMode}
            aria-label="Freihand zeichnen"
            title="Freihand zeichnen: Maustaste gedrückt halten und über den Chart ziehen"
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
              freehandMode ? "border-ink bg-ink text-card" : "border-hairline text-ink-secondary hover:bg-muted",
            )}
          >
            <PenTool className="h-4 w-4" />
          </button>
          {/* Radiergummi-CURSOR-Modus (neu, Feedback-Runde 16 Punkt 9) - eigener Toggle statt
              den bestehenden "Alles löschen"-Knopf umzuwidmen, damit beide Wege nebeneinander
              eindeutig unterscheidbar bleiben (Trash2 = sofort alles weg, Eraser = Modus zum
              gezielten Wegwischen einzelner Linien). */}
          <button
            type="button"
            onClick={toggleEraseMode}
            disabled={!hasDrawings && !eraseMode}
            aria-pressed={eraseMode}
            aria-label="Radiergummi-Modus: einzelne Linien durch Anklicken/Ziehen löschen"
            title={
              hasDrawings
                ? "Radiergummi-Modus: einzelne Linien durch Anklicken/Ziehen löschen"
                : "Keine Zeichnungen vorhanden"
            }
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent",
              eraseMode ? "border-ink bg-ink text-card" : "border-hairline text-ink-secondary hover:bg-muted",
            )}
          >
            <Eraser className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleEraseAll}
            disabled={!hasDrawings}
            aria-label="Alle Trendlinien und Freihand-Zeichnungen entfernen"
            title={hasDrawings ? "Alle Trendlinien & Freihand-Zeichnungen entfernen" : "Keine Zeichnungen vorhanden"}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-hairline text-ink-secondary transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {zoomRange ? (
            <button
              type="button"
              onClick={() => setZoomRange(null)}
              className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
            >
              Zoom zurücksetzen
            </button>
          ) : (
            <span className="hidden items-center gap-1 text-xs text-muted-foreground md:inline-flex">
              <ZoomIn className="h-3 w-3" aria-hidden="true" />
              Bereich ziehen zum Zoomen
            </span>
          )}
          <RangeSelector
            value={range}
            onChange={(r) => {
              setRange(r);
              setZoomRange(null);
            }}
          />
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-pressed={isExpanded}
            aria-label={isExpanded ? "Vollbildansicht verlassen" : "Chart vergrößern"}
            title={isExpanded ? "Vollbildansicht verlassen (Esc)" : "Chart vergrößern"}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-hairline text-ink-secondary transition-colors hover:bg-muted"
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {drawMode && (
        <p className="-mt-2 text-xs text-muted-foreground">
          {drawStart
            ? "Endpunkt der Trendlinie anklicken … (rastet nahe der Starthöhe automatisch waagerecht ein)"
            : "Startpunkt der Trendlinie auf dem Chart anklicken …"}
        </p>
      )}
      {freehandMode && (
        <p className="-mt-2 text-xs text-muted-foreground">
          Maustaste gedrückt halten und über den Chart ziehen, um freihändig zu zeichnen …
        </p>
      )}
      {eraseMode && (
        <p className="-mt-2 text-xs text-muted-foreground">
          Über eine Linie fahren und anklicken oder darüber ziehen, um sie zu löschen …
        </p>
      )}
      {!drawMode && !freehandMode && !eraseMode && (keyboardSelection.left !== null || keyboardSelection.right !== null) && (
        <p className="-mt-2 text-xs text-muted-foreground">
          Umschalttaste + Pfeiltasten passt die Auswahl an, Eingabetaste zoomt hinein, Escape verwirft …
        </p>
      )}

      {/* Vergrößerte Ansicht (backlog.md -> [Punkt 63]): eine feste "70vh" liess bei wenig
          zusaetzlichem Inhalt (Werkzeugleiste, ggf. Freihand-Liste) darunter ungenutzten
          Leerraum bis zum unteren Rand des Vollbild-Overlays stehen, da dessen eigentliche
          Hoehe (Viewport minus Werkzeugleiste/Padding) meist groesser als 70vh ausfaellt.
          `flex-1` innerhalb des `flex-col`-Overlays (siehe weiter unten) laesst den Chart
          stattdessen den tatsaechlich verfuegbaren Restplatz ausfuellen; `min-height: 0` ist
          fuer Flex-Kinder mit eigenem Overflow-Inhalt noetig, sonst wird der Flex-Item auf
          seinen Inhalt statt auf den verfuegbaren Platz begrenzt. */}
      <div
        className="relative w-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        style={isExpanded ? { flex: "1 1 0%", minHeight: 0 } : { height: "24rem" }}
        tabIndex={drawMode || freehandMode || eraseMode ? undefined : 0}
        role={drawMode || freehandMode || eraseMode ? undefined : "group"}
        aria-label={
          drawMode || freehandMode || eraseMode
            ? undefined
            : "Chart-Zeitraum. Umschalttaste mit Pfeiltasten wählt einen Zoom-Bereich, Eingabetaste übernimmt ihn, Escape verwirft ihn."
        }
        onKeyDown={handleChartKeyDown}
      >
        <PriceChartCore
          chartData={visibleChartData}
          axisTicks={axisTicks}
          range={range}
          showShort={showShort}
          showLong={showLong}
          logScale={logScale}
          showCandles={showCandles}
          priceFormatter={priceFormatter}
          onZoom={handleZoom}
          keyboardRefLeft={keyboardSelection.left}
          keyboardRefRight={keyboardSelection.right}
        />
        <div
          ref={overlayRef}
          className={cn(
            "absolute inset-0",
            eraseMode ? "cursor-erase" : drawMode || freehandMode ? "cursor-draw-crosshair" : "pointer-events-none",
          )}
          onClick={handleOverlayClick}
          onMouseDown={handleOverlayMouseDown}
          onMouseMove={handleOverlayMouseMove}
          onMouseUp={handleOverlayMouseUp}
          onMouseLeave={handleOverlayMouseUp}
        >
          {/* viewBox 0 0 100 100 + preserveAspectRatio="none": Koordinaten 0-100 mappen
              unabhaengig fuer x/y direkt auf die tatsaechliche Pixelgroesse des Containers -
              dieselbe Prozent-Logik wie bei den `line`-Elementen unten, nur als reine Zahlen
              nutzbar (das `points`-Attribut von `polyline` erlaubt anders als x1/y1 keine
              "%"-Strings). */}
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {trendLines.map((t) => {
              const isEraseHover = eraseHoverTarget?.kind === "trend" && eraseHoverTarget.id === t.id;
              return (
                <line
                  key={t.id}
                  x1={`${t.x1Pct}%`}
                  y1={`${t.y1Pct}%`}
                  x2={`${t.x2Pct}%`}
                  y2={`${t.y2Pct}%`}
                  stroke={isEraseHover ? "#B4544C" : "#8B5CF6"}
                  strokeWidth={isEraseHover ? 3 : 2}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
            {drawStart && drawPreview && (
              <>
                <line
                  x1={`${drawStart.xPct}%`}
                  y1={`${drawStart.yPct}%`}
                  x2={`${drawPreview.xPct}%`}
                  y2={`${drawPreview.yPct}%`}
                  stroke={drawSnapped ? "#C6A15B" : "#8B5CF6"}
                  strokeWidth={2}
                  strokeDasharray={drawSnapped ? undefined : "6 4"}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                {drawSnapped && (
                  <text
                    x={`${(drawStart.xPct + drawPreview.xPct) / 2}%`}
                    y={`${drawStart.yPct}%`}
                    dy={-6}
                    textAnchor="middle"
                    fill="#C6A15B"
                    fontSize={11}
                    vectorEffect="non-scaling-stroke"
                  >
                    waagerecht
                  </text>
                )}
              </>
            )}
            {freehandPaths.map((path) => {
              const isEraseHover = eraseHoverTarget?.kind === "freehand" && eraseHoverTarget.id === path.id;
              return (
                <polyline
                  key={path.id}
                  points={path.points.map((p) => `${p.xPct},${p.yPct}`).join(" ")}
                  fill="none"
                  stroke={isEraseHover ? "#B4544C" : "#8B5CF6"}
                  strokeWidth={isEraseHover ? 3 : 2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
            {currentFreehandPoints && currentFreehandPoints.length > 1 && (
              <polyline
                points={currentFreehandPoints.map((p) => `${p.xPct},${p.yPct}`).join(" ")}
                fill="none"
                stroke="#8B5CF6"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {/* Greifbare Endpunkte je Trendlinie (Erik 2026-08-20, [Punkt 65] konkretisiert:
              "an BEIDEN Endpunkten anfassbar/drehbar"). Nur im reinen Ansichtsmodus sichtbar
              (nicht waehrend Zeichnen/Freihand/Radiergummi - dort ist die Zeichenflaeche
              bereits anderweitig belegt). HTML-`div` statt SVG-`circle`: das umgebende SVG
              nutzt `preserveAspectRatio="none"` mit einem quadratischen 0-100-viewBox auf
              einem nicht-quadratischen Container - ein `circle` wuerde dadurch zur Ellipse
              verzerrt, ein absolut positionierter Div-Kreis mit fester Pixelgroesse bleibt
              rund. `pointer-events-auto` durchbricht gezielt das `pointer-events-none` des
              umgebenden Overlay-Divs (siehe dessen className oben) - NUR dieser kleine
              Griff-Bereich ist im Ansichtsmodus klickbar, der Rest der Flaeche bleibt
              durchlaessig fuer den bestehenden Klick-Zieh-Zoom des Charts darunter (behebt
              den gemeldeten Bug, ohne diese etablierte Zoom-Funktion anzutasten). */}
          {!drawMode && !freehandMode && !eraseMode && trendLines.map((t) => (
            <div key={`${t.id}-handles`}>
              <div
                aria-hidden="true"
                data-trend-handle="true"
                onMouseDown={(e) => handleEndpointMouseDown(e, t.id, 1)}
                className="pointer-events-auto absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-[#8B5CF6] shadow-sm transition-transform hover:scale-125 active:cursor-grabbing"
                style={{ left: `${t.x1Pct}%`, top: `${t.y1Pct}%` }}
              />
              <div
                aria-hidden="true"
                data-trend-handle="true"
                onMouseDown={(e) => handleEndpointMouseDown(e, t.id, 2)}
                className="pointer-events-auto absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-[#8B5CF6] shadow-sm transition-transform hover:scale-125 active:cursor-grabbing"
                style={{ left: `${t.x2Pct}%`, top: `${t.y2Pct}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      {trendLines.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {trendLines.map((t, i) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-muted px-3 py-1 text-xs text-ink-secondary"
              >
                Trendlinie {i + 1}
                {/* [Punkt 98] Touch-Zielflaeche vergroessert, kompensierendes negatives
                    Margin haelt die Pill-Groesse/-Ausrichtung unveraendert. */}
                <button
                  type="button"
                  onClick={() => persistTrendLines(trendLines.filter((l) => l.id !== t.id))}
                  aria-label={`Trendlinie ${i + 1} entfernen`}
                  className="-m-1.5 p-1.5 text-muted-foreground hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Eigene Trendlinien bleiben in diesem Browser gespeichert. An den violetten Punkten
            ziehen, um einen Endpunkt zu verschieben und die Linie neu auszurichten.
          </p>
        </div>
      )}

      {freehandPaths.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {freehandPaths.map((path, i) => (
              <span
                key={path.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-muted px-3 py-1 text-xs text-ink-secondary"
              >
                Freihand-Zeichnung {i + 1}
                <button
                  type="button"
                  onClick={() => persistFreehandPaths(freehandPaths.filter((p) => p.id !== path.id))}
                  aria-label={`Freihand-Zeichnung ${i + 1} entfernen`}
                  className="-m-1.5 p-1.5 text-muted-foreground hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Eigene Freihand-Zeichnungen bleiben in diesem Browser gespeichert.
          </p>
        </div>
      )}
    </>
  );

  if (isExpanded && typeof document !== "undefined") {
    return createPortal(
      <div className="fixed inset-0 z-50 flex flex-col gap-4 overflow-y-auto bg-background p-6">
        {content}
      </div>,
      document.body,
    );
  }

  return <div className="flex flex-col gap-4">{content}</div>;
}
